import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@material-ui/icons/Close';
import { isMobile } from 'react-device-detect';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import './CustomModal.css';

const CustomModal = React.forwardRef((props, ref) => {
  const { visible = false, children, onClose, help, title, description } = props;
  const [helpVisible, setHelpVisible] = useState(false);
  const onModalClick = e => e.stopPropagation();
  useEffect(() => {
    const checkECSKey = evt => {
      let isEscape = false;
      if ('key' in evt) {
        isEscape = (evt.key === 'Escape' || evt.key === 'Esc');
      } else {
        isEscape = (evt.keyCode === 27);
      }
      if (isEscape && onClose) {
        onClose();
      }
    };
    document.addEventListener('keydown', checkECSKey, false);
    return () => {
      document.removeEventListener('keydown', checkECSKey, false);
    };
  }, []);
  return (
    // @wassim commenting out onClick here prevents closing modal when user click outside
    // Every other part works well, kindly check if this will not break any other stuff
    <div className={`CustomModal ${visible ? 'CustomModal-open' : ''}`} /* onClick={onClose} dont close modal when user click outside */>
      <div className="CustomModal-modal">
        <div className="CustomModal-modal-container" onClick={onModalClick}>
          <div className="CustomModal-modal-head">
            { isMobile ? <div className="help-btn" onClick={() => setHelpVisible(true)}>Help <HelpOutlineIcon/></div> : null}
            <div className="CustomModal-modal-head-title">{title}</div>
            <span>{description}</span>
          </div>
          {children}
        </div>
      </div>
      {(!isMobile || helpVisible)
        ? (
          <div className={`CustomModal-help ${isMobile ? 'CustomModal-full-width' : ''} ${help ? 'CustomModal-help-open' : ''}`} onClick={onModalClick}>
            {isMobile
              ? (
                <span className="closeicon" onClick={() => setHelpVisible(false)}>
                  <CloseIcon/>
                </span>
              )
              : null}
            {help}
          </div>
        )
        : null}
    </div>
  );
});

CustomModal.Highlight = function (props) {
  const { help, children, margin = 0, helpSectionOnly, onMobileShowButton } = props;
  const ref = useRef();
  const refCustomModal = useRef();
  const [visible, setVisible] = useState(false);
  const [opened, setOpened] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState('');
  const scrollToView = eventName => (e) => {
    if (isMobile && eventName === 'focus') return false;
    if (!isMobile && eventName === 'click') return false;

    if (isMobile) {
      if (!e.target || !e.target.classList || !e.target.classList.length || !e.target.classList.contains('open-help')) {
        return false;
      }
    }

    if (refCustomModal && refCustomModal.current) {
      const rect = refCustomModal.current.getBoundingClientRect();
      const isInViewport = rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);
      if (!isInViewport) {
        refCustomModal.current.scrollIntoView(rect.bottom < (window.innerHeight || document.documentElement.clientHeight));
      }
    }
    return onFocus(e);
  };
  const onFocus = (e) => {
    const rect = refCustomModal.current.getClientRects()[0];
    const textarea = refCustomModal.current.querySelector('textarea');
    if (textarea) {
      setTextareaHeight(textarea.style.height);
    } else {
      setTextareaHeight('');
    }
    const name = e.target.tagName;
    const height = rect.height + margin * 2;
    setVisible({
      top: height > window.innerHeight ? 0 : rect.top - margin,
      right: rect.right - margin,
      bottom: rect.bottom - margin,
      left: rect.left - margin,
      width: rect.width + margin * 2,
      height: rect.height + margin * 2,
      maxHeight: '100vh',
      padding: margin
    });
    setTimeout(() => {
      setOpened(true);
      if (ref && ref.current) {
        const target = ref.current.querySelector(name);
        if (target) {
          if (target.querySelector('.ql-editor')) {
            target.querySelector('.ql-editor').focus();
          } else {
            target.focus();
          }
        }
        ref.current.classList.add('CustomModal-Highlight-component-open');
        const mainTag = document.querySelector('main');
        if (mainTag) {
          mainTag.classList.add('main-custom-modal-open');
        }
        setTimeout(() => {
          const m = ref.current.offsetParent.offsetWidth - ref.current.offsetLeft - ref.current.offsetWidth - 175;
          if (m < 0) {
            setVisible(v => ({ ...v, left: v.left - Math.abs(m) - 20 }));
          }
        }, 310);
      }
    }, 10);
    return false;
  };

  const close = () => {
    setOpened(false);
    setTimeout(() => {
      const mainTag = document.querySelector('main');
      if (mainTag) {
        mainTag.classList.remove('main-custom-modal-open');
      }
      setVisible(false);
    }, 200);
  };

  if (visible) {
    if (helpSectionOnly) {
      return (
        <div onBlur={close}>
          { children }
          <div className={`CustomModal-help-helpSectionOnly ${isMobile ? 'CustomModal-full-width' : ''} ${help ? 'CustomModal-help-helpSectionOnly-open' : ''}`} onClick={e => e.stopPropagation()}>
            {
              isMobile
                ? (
                  <span className="closeicon" onClick={close}>
                    <CloseIcon/>
                  </span>
                )
                : null
            }
            {help}
          </div>
        </div>
      );
    }
    return (
      <div style={{ height: visible.height - (margin * 2) }}>
        <style>{`.CustomModal-Highlight-open textarea { height : ${textareaHeight}; }`}</style>
        <div className={`CustomModal-Highlight ${opened ? 'CustomModal-Highlight-open' : ''}`} onClick={close}>
          <div className="CustomModal-Highlight-component" style={{ ...visible }} ref={ref} onClick={e => e.stopPropagation()}>
            { children }
          </div>
          <div className={`CustomModal-help ${isMobile ? 'CustomModal-full-width' : ''} ${help ? 'CustomModal-help-open' : ''}`} onClick={e => e.stopPropagation()}>
            {
              isMobile
                ? (
                  <span className="closeicon" onClick={close}>
                    <CloseIcon/>
                  </span>
                )
                : null
            }
            {help}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div ref={refCustomModal} className="CustomModal-hl" onFocus={scrollToView('focus')} onClick={(isMobile && !onMobileShowButton) ? scrollToView('click') : undefined}>
      { children }
      { isMobile && onMobileShowButton ? <a className="open-help-button open-help" onClick={scrollToView('click')}>{ onMobileShowButton }</a> : null }
    </div>
  );
};

CustomModal.Highlight.propTypes = {
  children: PropTypes.any,
  help: PropTypes.any,
  helpSectionOnly: PropTypes.any,
  onMobileShowButton: PropTypes.any,
  margin: PropTypes.number
};

CustomModal.propTypes = {
  children: PropTypes.any,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  help: PropTypes.any,
  title: PropTypes.string,
  description: PropTypes.string
};

export default CustomModal;
